import { Button, Drawer, Label } from "@components";
import { useEffect, useMemo, useRef, useCallback, memo, useState } from "react";
import { Phone } from "shared/utils/Phone";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import SearchAddressesLocation from "shared/containers/SearchAddressLocation";
import styles from "../../../styles/service/service.module.scss";
import { If } from "shared/components/control-statements";
import { v4 as uuid } from "uuid";

const markerIcon = new L.Icon({
  iconUrl: require("../../../assets/location.png"),
  iconAnchor: [25, 50],
});

const ChangeMapView = memo(({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (!center?.[0] || !center?.[1]) return;
    map.setView(center, map.getZoom(), {
      animate: true,
    });
  }, [center, map]);

  return null;
});

const DraggableMarker = memo(({ position, readOnly, onChangePosition }) => {
  const markerRef = useRef(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (!marker) return;

        const { lat, lng } = marker.getLatLng();

        onChangePosition?.({
          lat,
          lng,
        });
      },
    }),
    [onChangePosition],
  );

  return (
    <Marker
      draggable={!readOnly}
      position={position}
      eventHandlers={eventHandlers}
      ref={markerRef}
      icon={markerIcon}
    />
  );
});

const AddAddress = ({ open, onClose, customer, address, onAddAddress }) => {
  const [selectedAddress, setSelectedAddress] = useState(address);

  useEffect(() => {
    setSelectedAddress(address);
  }, [address, open]);

  const position = useMemo(() => {
    const lat = selectedAddress?.geolocation?.latitude;
    const lng = selectedAddress?.geolocation?.longitude;

    if (!lat || !lng) return null;

    return [lat, lng];
  }, [selectedAddress]);

  const handleMarkerChange = useCallback(({ lat, lng }) => {
    setSelectedAddress((prev) => ({
      ...prev,
      geolocation: {
        ...prev?.geolocation,
        latitude: lat,
        longitude: lng,
      },
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedAddress) return;

    onAddAddress({
      id: uuid(),
      ...selectedAddress,
    });

    onClose();
  }, [selectedAddress, onAddAddress, onClose]);

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      title="Adicionar endereço"
      subtitle={Phone.mask(customer?.phone || "")}
      showBackButton
      backButton={onClose}
    >
      <Label className="mb-0" required>
        Insira um endereço:
      </Label>

      <SearchAddressesLocation
        setSelectedAddress={setSelectedAddress}
        selectedAddress={selectedAddress}
      />

      <If cond={!!position}>
        <div className={styles.mapContainer}>
          <div className={styles.map}>
            <MapContainer
              style={{ width: "100%", height: "100%" }}
              center={position}
              zoom={15}
              minZoom={15}
              maxZoom={18}
              attributionControl={false}
              zoomControl={false}
              doubleClickZoom
              scrollWheelZoom
            >
              <ChangeMapView center={position} />

              <TileLayer
                url="https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&apistyle=s.e%3Ag%7Cp.c%3A%23ff242f3e%2Cs.e%3Al.t.f%7Cp.c%3A%23ff746855%2Cs.e%3Al.t.s%7Cp.c%3A%23ff242f3e%2Cs.t%3A19%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A2%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A40%7Cs.e%3Ag%7Cp.c%3A%23ff263c3f%2Cs.t%3A40%7Cs.e%3Al.t.f%7Cp.c%3A%23ff6b9a76%2Cs.t%3A3%7Cs.e%3Ag%7Cp.c%3A%23ff38414e%2Cs.t%3A3%7Cs.e%3Ag.s%7Cp.c%3A%23ff212a37%2Cs.t%3A3%7Cs.e%3Al.t.f%7Cp.c%3A%23ff9ca5b3%2Cs.t%3A49%7Cs.e%3Ag%7Cp.c%3A%23ff746855%2Cs.t%3A49%7Cs.e%3Ag.s%7Cp.c%3A%23ff1f2835%2Cs.t%3A49%7Cs.e%3Al.t.f%7Cp.c%3A%23fff3d19c%2Cs.t%3A4%7Cs.e%3Ag%7Cp.c%3A%23ff2f3948%2Cs.t%3A66%7Cs.e%3Al.t.f%7Cp.c%3A%23ffd59563%2Cs.t%3A6%7Cs.e%3Ag%7Cp.c%3A%23ff17263c%2Cs.t%3A6%7Cs.e%3Al.t.f%7Cp.c%3A%23ff515c6d%2Cs.t%3A6%7Cs.e%3Al.t.s%7Cp.c%3A%23ff17263c"
                attribution=""
              />

              <DraggableMarker
                position={position}
                onChangePosition={handleMarkerChange}
              />
            </MapContainer>
          </div>
        </div>
      </If>

      <If cond={!position}>
        <div className={styles.mapEmpty}>
          <svg
            width="32"
            height="28"
            viewBox="0 0 32 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.3667 8.47437C20.7389 7.60506 20.8889 6.96539 20.8889 6.5608C20.8889 3.90368 18.7 1.74955 16 1.74955C13.3 1.74955 11.1111 3.90368 11.1111 6.5608C11.1111 6.96539 11.2611 7.5996 11.6333 8.47437C11.9944 9.31634 12.5056 10.2458 13.1 11.1916C14.05 12.7116 15.15 14.1823 16 15.2593C16.85 14.1823 17.95 12.7116 18.9 11.1916C19.4944 10.2458 20.0056 9.31634 20.3667 8.47437ZM16.8222 17.0581C16.3944 17.583 15.6 17.583 15.1778 17.0581C13.3944 14.8657 9.33333 9.54597 9.33333 6.5608C9.33333 2.93596 12.3167 0 16 0C19.6833 0 22.6667 2.93596 22.6667 6.5608C22.6667 9.54597 18.6056 14.8657 16.8222 17.0581ZM22.55 12.1867C22.5056 12.2031 22.4556 12.2195 22.4111 12.2304C22.8667 11.4541 23.2833 10.6613 23.6222 9.88494L30.1722 7.30436C31.05 6.95992 32 7.59413 32 8.52358V23.3291C32 23.8649 31.6667 24.346 31.1611 24.5483L22.55 27.9381C22.3667 28.0092 22.1667 28.0201 21.9778 27.9654L9.82778 24.5429L1.82778 27.6921C0.95 28.0365 0 27.4023 0 26.4728V11.6673C0 11.1315 0.333333 10.6504 0.838889 10.4481L7.68333 7.75268C7.8 8.32129 7.98333 8.88989 8.19444 9.43662L1.77778 11.9625V25.8222L8.88889 23.0229V16.6207C8.88889 16.1396 9.28889 15.7459 9.77778 15.7459C10.2667 15.7459 10.6667 16.1396 10.6667 16.6207V22.9573L21.3333 25.9589V16.6207C21.3333 16.1396 21.7333 15.7459 22.2222 15.7459C22.7111 15.7459 23.1111 16.1396 23.1111 16.6207V25.8277L30.2222 23.0284V9.16872L22.55 12.1867ZM16 4.81126C16.1751 4.81126 16.3485 4.8452 16.5102 4.91114C16.672 4.97708 16.819 5.07373 16.9428 5.19558C17.0666 5.31742 17.1648 5.46208 17.2318 5.62127C17.2988 5.78047 17.3333 5.9511 17.3333 6.12342C17.3333 6.29573 17.2988 6.46636 17.2318 6.62556C17.1648 6.78476 17.0666 6.92941 16.9428 7.05125C16.819 7.1731 16.672 7.26975 16.5102 7.33569C16.3485 7.40164 16.1751 7.43558 16 7.43558C15.8249 7.43558 15.6515 7.40164 15.4898 7.33569C15.328 7.26975 15.181 7.1731 15.0572 7.05125C14.9334 6.92941 14.8352 6.78476 14.7682 6.62556C14.7012 6.46636 14.6667 6.29573 14.6667 6.12342C14.6667 5.9511 14.7012 5.78047 14.7682 5.62127C14.8352 5.46208 14.9334 5.31742 15.0572 5.19558C15.181 5.07373 15.328 4.97708 15.4898 4.91114C15.6515 4.8452 15.8249 4.81126 16 4.81126Z"
              fill="#4C4C4C"
            />
          </svg>
        </div>
      </If>

      <div className={styles.mapFooter}>
        <Button
          className="w-100"
          design="primary"
          onClick={handleConfirm}
          disabled={!selectedAddress}
        >
          Confirmar
        </Button>
      </div>
    </Drawer>
  );
};

export default AddAddress;
